import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function CreditAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Credit API" description="Search organizations and retrieve detailed credit ratings history with the Credit API. Access institutional credit ratings data for risk and investment analysis."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/credit-ratings/00-search" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Credit API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Credit API provides access to institutional credit ratings data, allowing you to search for organizations and retrieve their detailed credit ratings history. This data is essential for credit risk analysis, investment due diligence, and regulatory compliance.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Search Organizations */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /credit/search
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Searches for organizations by name and returns matching entities with their basic identification information. This endpoint is useful for discovering the correct organization ID needed to retrieve detailed credit ratings.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">query</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Organization name or identifier to search for. Supports partial matches.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/credit/search?query=AAPL', '/credit/search?query=MSFT&limit=20']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Unique organization identifier</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full organization name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">sectorCode <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Industry sector code (e.g., &quot;CORP&quot; for Corporate)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">country <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Country of domicile</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">state <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">State or province (if applicable)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">navigationUrl <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">URL for detailed organization page</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Search Organizations" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Search Organizations</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="credit/search"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/credit-ratings/00-search"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
